import { type FC, forwardRef } from 'react';
import classNames from 'classnames/bind';
import { type CardRadioPropsTypes } from '../types/propsType';
import { FAIcon } from '../../Icons/Icons';
import { MLPVersion, Theme } from '../../types/enums';
import RadioButton from '../RadioButton';
import { RadioButtonAlignment, RadioIconTypes } from '../types/enums';
import { NextgenImage } from '../../NextgenImage/NextgenImage';
import styles from './CardRadioButton.module.scss';

const cx = classNames.bind(styles);

const CardRadioButton: FC<CardRadioPropsTypes> = forwardRef<HTMLInputElement, CardRadioPropsTypes>(
  (
    {
      label,
      disabled,
      theme = Theme.BLACK,
      className = '',
      icon,
      cardDescription,
      swatchColor,
      image,
      mlpVersion = MLPVersion.TWO,
      value,
      children,
      divider = false,
      radioButtonAlignment = RadioButtonAlignment.CENTER,
      name = 'cardRadioButton',
      ...restProps
    },
    ref,
  ) => {
    let radioIconTemplate: React.ReactNode;
    let styleTemplate: React.ReactNode;
    const { ICON, SWATCHCOLOR, IMAGE, VALUE } = RadioIconTypes;
    const radioIconOption = (icon && ICON) || (image && IMAGE) || (swatchColor && SWATCHCOLOR) || (value && VALUE);
    const radioButtonId = `${name}-${Math.floor(1000 + Math.random() * 9000)}`;

    switch (radioIconOption) {
      case ICON:
        radioIconTemplate = icon && (
          <FAIcon
            {...icon}
            theme={theme}
            data-testid="icon-test-id"
            className={`${styles['icon']} ${styles['left-icon']}`}
          />
        );
        break;
      case SWATCHCOLOR: {
        const cSwatchClassname = cx({ disabled: disabled }, 'swatch', 'left-icon');
        radioIconTemplate = swatchColor && (
          <div style={{ background: `${swatchColor}` }} data-testid="swatch-test-id" className={cSwatchClassname}>
            {disabled && <span className={styles['strikeThrough']} data-testid="line-test-id"></span>}
          </div>
        );
        break;
      }
      case IMAGE:
        radioIconTemplate = image && (
          <div className={styles['image']}>
            <NextgenImage
              src={`${image}`}
              className={`${styles['left-icon']}`}
              alt="card radio icon"
              data-testid="image-test-id"
            />
          </div>
        );
        break;
      case VALUE:
        radioIconTemplate = value && (
          <span className={styles['card-value']} data-testid="value-test-id">
            {value}
          </span>
        );
        break;
      default:
        break;
    }

    switch (mlpVersion) {
      case MLPVersion.ONE:
        styleTemplate = (
          <div className={styles['card-radio-upper']}>
            <span className={styles['card-content']}>
              {radioIconTemplate}
              <span>
                <section>
                  {label && (
                    <label className={styles['card-header']} htmlFor={restProps.id || radioButtonId}>
                      {label}
                    </label>
                  )}
                </section>
                <section>
                  {cardDescription && <span className={styles['card-details']}>{cardDescription}</span>}
                </section>
              </span>
            </span>
            <span className={`${styles['card-radio-button']} ${styles[radioButtonAlignment]}`}>
              <RadioButton
                id={restProps.id || radioButtonId}
                theme={theme}
                disabled={disabled}
                forwardedRef={ref}
                {...restProps}
              />
            </span>
          </div>
        );
        break;
      default:
        styleTemplate = (
          <div className={styles['card-radio-upper']}>
            <div className={styles['card-radio-MLP2']}>
              <RadioButton id={restProps.id || radioButtonId} theme={theme} disabled={disabled} {...restProps} />
              <span>
                <section>
                  {label && (
                    <label htmlFor={restProps.id || radioButtonId} className={styles['card-header']}>
                      {label}
                    </label>
                  )}
                </section>
                <section>
                  {cardDescription && <span className={styles['card-details']}>{cardDescription}</span>}
                </section>
              </span>
            </div>
            <span className={styles['card-content']}>{radioIconTemplate}</span>
          </div>
        );
        break;
    }
    const CClassname = cx(
      'card-radio-container',
      { ...(disabled ? { disabled: true } : { [theme]: true }), 'card-radio-button-checked': restProps.checked },
      className,
    );
    const CChildrenClassname = cx('card-radio-children-container', { 'card-radio-children-divider': divider });
    return (
      <label className={CClassname} data-testid="card-radio-test-id">
        {styleTemplate}
        {children && (
          <div className={CChildrenClassname} data-testid="children-body-test-id">
            {children}
          </div>
        )}
      </label>
    );
  },
);
CardRadioButton.displayName = 'CardRadioButton';
export { RadioButtonAlignment };
export default CardRadioButton;
